import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { errorToast, GuestHeader, TimedLoader } from "@/components";
import { PasswordInput } from "@/components/profiling/password-input";
import { useSetPassword } from "@/domains/profile/mutations/use-set-password";
import { env } from "@/env";
import { Button, Heading } from "@/ui";
import {
  testPasswordContainsNumber,
  testPasswordContainsSpecialCharacter,
  testPasswordContainsUppercaseLetter,
  testPasswordLength,
} from "@/utils";
import { validateError } from "@/utils/validation";

const schema = z
  .object({ password: z.string(), passwordConfirmation: z.string() })
  .refine(
    (data) => {
      return testPasswordLength(data.password);
    },
    {
      message: "The password is too short",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsUppercaseLetter(data.password);
    },
    {
      message: "The password must contain a capital letter",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsNumber(data.password);
    },
    {
      message: "The password must contain a number",
      path: ["password"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsSpecialCharacter(data.password);
    },
    {
      message: "The password must contain a special character",
      path: ["password"],
    },
  )
  .superRefine(({ password, passwordConfirmation }, ctx) => {
    if (password !== passwordConfirmation) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords do not match",
        path: ["passwordConfirmation"],
      });
    }
  });

type SetUserPasswordData = z.infer<typeof schema>;

const Password = () => {
  const [shouldAutoComplete, setShouldAutoComplete] = useState(false);

  const { isPending, mutate } = useSetPassword();

  const { id: variantRevisionUuid } = Route.useSearch();

  const {
    clearErrors,
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = useForm<SetUserPasswordData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: SetUserPasswordData) => {
    clearErrors();
    mutate(
      {
        variantRevisionUuid: variantRevisionUuid!,
        password: data.password,
        passwordConfirmation: data.passwordConfirmation,
      },
      {
        onSuccess: () => {
          setShouldAutoComplete(true);
        },
        onError: (error) => {
          const validatedError = validateError(error);

          if (validatedError) {
            const fieldErrors = validatedError.response.data.error.fields;

            if (fieldErrors) {
              Object.entries(fieldErrors).forEach(([field, messages]) => {
                setError(field as keyof SetUserPasswordData, {
                  type: "manual",
                  message: messages.join("\n"),
                });
              });
            } else {
              errorToast(error);
            }
          }
        },
      },
    );
  };

  if (isPending || shouldAutoComplete) {
    return (
      <TimedLoader shouldAutoComplete={shouldAutoComplete}>
        <div className="flex flex-col gap-6">
          <Heading className="text-xl sm:text-3xl" level={1}>
            {`That's it! We're creating your plan.`}
          </Heading>
          <p className="text-xl font-medium">
            Do not refresh this page. This should take no more than 30 seconds.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <Heading level={2}>{`What's Next?`}</Heading>
          <ul className="list-inside list-disc text-xl font-medium">
            <li>Review your plan</li>
            <li>Order your products</li>
            <li>Use &amp; provide feedback</li>
          </ul>
        </div>
      </TimedLoader>
    );
  }

  return (
    <div className="flex min-h-dvh w-full flex-col">
      <GuestHeader logoRedirectUrl={env.VITE_MARKETING_SITE_DOMAIN} />
      <div className="flex w-full flex-1 justify-center bg-canvas">
        <section className="flex w-full max-w-xl flex-col justify-center px-5 py-12 sm:px-6 sm:py-16">
          <div className="flex flex-col gap-3 pb-8">
            <Heading level={1}>Got it - thanks! Just one more thing:</Heading>
            <p className="text-lg leading-relaxed text-content-secondary sm:text-xl">
              To access & save your plan, please choose a password:
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-5">
              <PasswordInput
                className="h-14"
                error={errors.password?.message}
                id="password"
                label="Password"
                placeholder="Enter password"
                showDisplayPasswordButton
                {...register("password")}
              />

              <PasswordInput
                className="h-14"
                error={errors.passwordConfirmation?.message}
                id="passwordConfirmation"
                label="Confirm password"
                placeholder="Re-enter password"
                showDisplayPasswordButton
                {...register("passwordConfirmation")}
              />
            </div>

            <Button className="mt-8 w-full" disabled={isPending} size="lg" type="submit">
              Save & View Your Care Plan
            </Button>
          </form>
        </section>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_guest/profiling/password/")({
  beforeLoad: async ({ search }) => {
    if (!search.id) {
      throw redirect({ to: "/profiling" });
    }
  },
  component: Password,
  validateSearch: z.object({ id: z.string() }),
});
